import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { VehicleMediaFormData } from './VehicleMediaForm'
import { UpsertVehicleMediaRequest } from '@nxcr-org/web-api-interface/lib/fleet_service_pb'
import { NexusService } from 'global-apis/nexus-service'

interface VehicleMediaRecord {
  id?: string
  client_id?: string
  make: string
  model: string
  trim: string
  year: number
  data: string
  created_at?: string
}

export const useVehicleMedia = (clientId: string) => {
  // Filter states
  const [make, setMake] = useState('')
  const [model, setModel] = useState('')
  const [year, setYear] = useState('')
  const [trim, setTrim] = useState('')

  // Drawer states
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false)
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState<VehicleMediaRecord | null>(null)
  
  // Delete modal states
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [deletingRecord, setDeletingRecord] = useState<VehicleMediaRecord | null>(null)

  const queryClient = useQueryClient()

  // Query for vehicle media data
  const {
    data: vehicleMediaData,
    isLoading,
    refetch
  } = useQuery({
    queryKey: ['vehicleMedia', clientId, make, model, year, trim],
    queryFn: async () => {
      if (!clientId) return { vehicleMediaList: [] }

      try {
        const response = await NexusService.getVehicleMedia(
          clientId,
          make,
          model,
          year,
          trim,
        )

        // Ensure the response has the expected structure
        return {
          vehicleMediaList: response?.mediaList || []
        }
      } catch (error) {
        console.error('Error fetching vehicle media:', error)
        return { vehicleMediaList: [] }
      }
    },
      enabled: !!clientId,
      staleTime: 30000, // 30 seconds
    }
  )

  // Mutation for upserting vehicle media
  const upsertMutation = useMutation({
    mutationFn: async (data: VehicleMediaFormData) => {
      const request = new UpsertVehicleMediaRequest()
      if (data.id) {
        request.setId(data.id)
      }
      request.setMake(data.make)
      request.setModel(data.model)
      request.setTrim(data.trim)
      request.setYear(data.year)
      request.setData(data.data)

      return await NexusService.upsertVehicleMedia({
        clientId,
        upsertVehicleMediaRequest: request
      })
    },
      onSuccess: () => {
        // Refetch data after successful upsert
        queryClient.invalidateQueries({queryKey: ['vehicleMedia']})
        setIsAddDrawerOpen(false)
        setIsEditDrawerOpen(false)
        setEditingRecord(null)
      },
      onError: (error) => {
        console.error('Error upserting vehicle media:', error)
      }
    }
  )

  // Mutation for deleting vehicle media
  const deleteMutation = useMutation({
    mutationFn: async (vehicleMediaId: string) => {
      return await NexusService.deleteVehicleMedia(vehicleMediaId)
    },
    onSuccess: () => {
      // Refetch data after successful delete
      queryClient.invalidateQueries({queryKey: ['vehicleMedia']})
      setIsDeleteDialogOpen(false)
      setDeletingRecord(null)
    },
    onError: (error) => {
      console.error('Error deleting vehicle media:', error)
    }
  })

  const handleSearch = () => {
    refetch()
  }

  const handleEdit = (record: VehicleMediaRecord) => {
    // Always set the editing record and open the drawer
    setEditingRecord(record)
    setIsEditDrawerOpen(true)
  }

  const handleDelete = (record: VehicleMediaRecord) => {
    setDeletingRecord(record)
    setIsDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (deletingRecord?.id) {
      deleteMutation.mutate(deletingRecord.id)
    }
  }

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false)
    setDeletingRecord(null)
  }

  return {
    // Data
    vehicleMediaList: vehicleMediaData?.vehicleMediaList || [],
    isLoading,

    // Filter states
    make,
    setMake,
    model,
    setModel,
    year,
    setYear,
    trim,
    setTrim,

    // Drawer states
    isAddDrawerOpen,
    setIsAddDrawerOpen,
    isEditDrawerOpen,
    setIsEditDrawerOpen,
    editingRecord,
    setEditingRecord,

    // Delete modal states
    isDeleteDialogOpen,
    deletingRecord,

    // Actions
    handleSearch,
    handleEdit,
    handleDelete,
    handleDeleteConfirm,
    handleDeleteCancel,
    upsertMutation,
    deleteMutation
  }
}
